import "@/scss/styles.scss";
import { useState, useEffect } from "react";
import { AppTemplate } from "@/ui/AppTemplate";
import { useLocale } from "@/hooks/useLocale";
import SourceFlowHead from "@sourceflow-uk/sourceflow-head";
import metaObject from "../../.sourceflow/metadata.json";
import sourceflowEnums from "@/sourceflow.enums";
import { useRouter } from "next/router";
import CookieBanner from "@/components/CookieBanner";
import CookiePreferencesModal from "@/components/CookiePreferencesModal";
import Script from "next/script";

const DEFAULT_META_PROPS = {
  metaObject,
  siteName: sourceflowEnums.config.siteName,
  defaultTitle: sourceflowEnums.config.siteName,
  images: [{ url: sourceflowEnums.config.socialLogo }],
  addTracker: true,
  cookieExpiryTime: 3000,
  robots: "index, follow",
};

export default function App({ Component, pageProps }) {
  const locale = useLocale();
  const metaProps =
    "meta" in pageProps ? Object.assign({}, DEFAULT_META_PROPS, pageProps?.meta[locale]) : DEFAULT_META_PROPS;

  const router = useRouter();
  const [isPreferenceModalOpen, setIsPreferenceModalOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);

  // Check if preferences exist on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('cookiePreferences');
      setShowBanner(!stored);
    }
  }, []);

  const handleSaveCookiePreferences = (preferences) => {
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    setShowBanner(false);
  };

  // Exclude `/components` page from being wrapped in Layout and SourceFlowHead
  const isComponentsPage = router.pathname === "/components";
  return isComponentsPage ? (
    <>
      <AppTemplate router={pageProps.router} sidebar={pageProps.sidebar} footer={pageProps.footer}>
        <Component {...pageProps} />
      </AppTemplate>
    </>
  ) : (
    <>
      <Script
        id="ga4"
        src="https://www.googletagmanager.com/gtag/js?id=G-WVTVJKJG0G"
        strategy="afterInteractive"
      />
      <Script id="ga4-init" strategy="afterInteractive">{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-WVTVJKJG0G');
      `}</Script>
      <Script id="microsoft-clarity" strategy="afterInteractive">{`
        (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "stlv3c65os");
      `}</Script>
      <Script id="herefish" strategy="afterInteractive">{`
        window.hfAccountId = "19ea56dd-4b05-47b1-9f52-11ae38ce961b";
        window.hfDomain = "https://api.herefish.com";
        (function() {
          var hf = document.createElement('script'); hf.type = 'text/javascript'; hf.async = true;
          hf.src = window.hfDomain + '/scripts/hf.js';
          var s = document.getElementsByTagName('script')[0]; s.parentNode.insertBefore(hf, s);
        })();
      `}</Script>
      <SourceFlowHead
        {...metaProps}
        title={`${metaProps.title} | ${metaProps.siteName}`}
        languageAlternates={[{ href: metaProps.canonical, hrefLang: locale }]} // explicitly set alternate
      />
      <CookiePreferencesModal 
        isOpen={isPreferenceModalOpen}
        onClose={() => setIsPreferenceModalOpen(false)}
        onSave={handleSaveCookiePreferences}
      />
      <CookieBanner 
        isOpen={showBanner}
        onSave={handleSaveCookiePreferences}
        onManageClick={() => setIsPreferenceModalOpen(true)}
      />
      <AppTemplate router={pageProps.router} sidebar={pageProps.sidebar} footer={pageProps.footer}>
        <Component {...pageProps} />
      </AppTemplate>
    </>
  );
}
